import type { AppDescription } from "../common/components/SerializedApp";
import { getSourceMetadata } from "../common/components/SerializedApp";
import type { TableName } from "../common/database-strings";
import { areTableNamesEqual } from "../common/database-strings";
import type { TableGlideType, TypeSchema } from "../common/description";
import { getTableName } from "../common/description";
import type { SourceMetadata } from "../common/source-metadata";
import { getGoogleSheetsSourceMetadata } from "../common/source-metadata";
import { assert, assertNever, isArray } from "../support";

export function renameTableInSchema(schema: TypeSchema, tableName: TableName, newName: string): TypeSchema {
    const { tables } = schema;
    const newTables = tables.map(t => {
        if (!areTableNamesEqual(getTableName(t), tableName)) return t;

        return { ...t, sheetName: newName };
    });
    return { ...schema, tables: newTables };
}

// `false` means Google Sheets
export function getMaybeSourceMetadataForTable(
    appOrMetadata: AppDescription | readonly SourceMetadata[],
    tableName: TableName | false
): SourceMetadata | undefined {
    if (!isArray(appOrMetadata)) {
        appOrMetadata = getSourceMetadata(appOrMetadata);
    }

    if (tableName !== false) {
        for (const m of appOrMetadata) {
            if (m.type === "Native table" && areTableNamesEqual(tableName, m.tableName)) {
                return m;
            }
        }
    }

    return getGoogleSheetsSourceMetadata(appOrMetadata);
}

// `false` means Google Sheets
export function getSourceMetadataForTable(
    appOrMetadata: AppDescription | readonly SourceMetadata[],
    tableName: TableName
): SourceMetadata;
export function getSourceMetadataForTable(
    appOrMetadata: AppDescription | readonly SourceMetadata[],
    tableName: TableName | false
): SourceMetadata | undefined;
export function getSourceMetadataForTable(
    appOrMetadata: AppDescription | readonly SourceMetadata[],
    tableName: TableName | false
): SourceMetadata | undefined {
    const metadata = getMaybeSourceMetadataForTable(appOrMetadata, tableName);
    if (metadata === undefined) {
        assert(tableName === false, `No such table ${JSON.stringify(tableName)}`);
    }
    return metadata;
}

export function canAddRowIDColumn(table: TableGlideType): boolean {
    const externalSource = table.sourceMetadata?.externalSource;
    if (externalSource === undefined) return true;
    switch (externalSource.type) {
        case "airtable":
        case "mysql-gcp":
            return true;
        case "excel-online":
        case "unknown":
            return false;
        default:
            return assertNever(externalSource);
    }
}
